import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import { validateResponse } from "@/api/validateResponse";
import {
  QuoteListResponseSchema,
  QuoteSchema,
  type CreateQuoteInput,
  type PatchQuoteInput,
  type Quote,
  type QuoteStatus,
} from "@/api/types/quote";

/** Query-key factory — mirrors the inquiries hook's convention. */
export const quoteKeys = {
  all: ["quotes"] as const,
  lists: () => [...quoteKeys.all, "list"] as const,
  list: (params: { inquiry_id?: string; status?: QuoteStatus }) =>
    [...quoteKeys.lists(), params] as const,
  details: () => [...quoteKeys.all, "detail"] as const,
  detail: (id: string | undefined) => [...quoteKeys.details(), id] as const,
};

function invalidateAll(queryClient: QueryClient) {
  void queryClient.invalidateQueries({ queryKey: quoteKeys.all });
}

interface UseQuotesParams {
  inquiry_id?: string;
  status?: QuoteStatus;
}

/** Flat list — the contract has no cursor field on GET /v2/quotes. */
export function useQuotes(params: UseQuotesParams = {}) {
  const { inquiry_id, status } = params;

  return useQuery<Quote[]>({
    queryKey: quoteKeys.list(params),
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      if (inquiry_id) searchParams.set("inquiry_id", inquiry_id);
      if (status) searchParams.set("status", status);
      const qs = searchParams.toString();
      const { data } = await apiClient.get(`/v2/quotes${qs ? `?${qs}` : ""}`);
      return validateResponse(
        QuoteListResponseSchema,
        data,
        "GET /v2/quotes",
      ).items;
    },
    staleTime: 15 * 1000,
  });
}

export function useQuote(quoteId: string | undefined) {
  return useQuery<Quote>({
    queryKey: quoteKeys.detail(quoteId),
    queryFn: async () => {
      const { data } = await apiClient.get(`/v2/quotes/${quoteId}`);
      return validateResponse(QuoteSchema, data, "GET /v2/quotes/:id");
    },
    enabled: !!quoteId,
  });
}

export function useCreateQuote() {
  const queryClient = useQueryClient();
  return useMutation<Quote, unknown, CreateQuoteInput>({
    mutationFn: async (input) => {
      const { data } = await apiClient.post("/v2/quotes", input);
      return validateResponse(QuoteSchema, data, "POST /v2/quotes");
    },
    onSuccess: (quote) => {
      queryClient.setQueryData(quoteKeys.detail(quote.id), quote);
      invalidateAll(queryClient);
    },
  });
}

export function usePatchQuote() {
  const queryClient = useQueryClient();
  return useMutation<Quote, unknown, PatchQuoteInput>({
    mutationFn: async ({ id, ...patch }) => {
      const { data } = await apiClient.patch(`/v2/quotes/${id}`, patch);
      return validateResponse(QuoteSchema, data, "PATCH /v2/quotes/:id");
    },
    onSuccess: (quote) => {
      queryClient.setQueryData(quoteKeys.detail(quote.id), quote);
      invalidateAll(queryClient);
    },
  });
}

function useQuoteAction(endpoint: string, label: string) {
  const queryClient = useQueryClient();
  return useMutation<Quote, unknown, { id: string; body?: Record<string, unknown> }>({
    mutationFn: async ({ id, body }) => {
      const { data } = await apiClient.post(`/v2/quotes/${id}/${endpoint}`, body ?? {});
      return validateResponse(QuoteSchema, data, `POST /v2/quotes/:id/${label}`);
    },
    onSuccess: (quote) => {
      queryClient.setQueryData(quoteKeys.detail(quote.id), quote);
      invalidateAll(queryClient);
    },
  });
}

export function useSendQuote() {
  return useQuoteAction("send", "send");
}

export function useMarkQuoteViewed() {
  return useQuoteAction("viewed", "viewed");
}

export function useAcceptQuote() {
  return useQuoteAction("accept", "accept");
}

export interface DeclineQuoteInput {
  id: string;
  reason: string;
}

export function useDeclineQuote() {
  const queryClient = useQueryClient();
  return useMutation<Quote, unknown, DeclineQuoteInput>({
    mutationFn: async ({ id, reason }) => {
      const { data } = await apiClient.post(`/v2/quotes/${id}/decline`, { reason });
      return validateResponse(QuoteSchema, data, "POST /v2/quotes/:id/decline");
    },
    onSuccess: (quote) => {
      queryClient.setQueryData(quoteKeys.detail(quote.id), quote);
      invalidateAll(queryClient);
    },
  });
}
